import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import './Carousel.css';

/**
 * Horizontally scrollable row with left/right chevrons that overlay the track
 * edges. The scrollbar is hidden; each chevron only renders while that side
 * has overflowed content.
 */
export type CarouselProps = {
  children: ReactNode;
  className?: string;
  /** Index of the track child to bring into view when `scrollIntoViewNonce` changes. */
  scrollIntoViewIndex?: number | null;
  /** Increment to re-run scroll even when the index is unchanged. */
  scrollIntoViewNonce?: number;
};

export function Carousel({
  children,
  className = '',
  scrollIntoViewIndex = null,
  scrollIntoViewNonce = 0,
}: CarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkOverflow, { passive: true });
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      ro.disconnect();
    };
  }, [checkOverflow]);

  // New children change scrollWidth without resizing the track itself (which
  // is all ResizeObserver watches), so re-check whenever they change.
  useEffect(() => {
    checkOverflow();
  }, [children, checkOverflow]);

  useEffect(() => {
    if (!scrollIntoViewNonce || scrollIntoViewIndex == null || scrollIntoViewIndex < 0) return undefined;
    const track = scrollRef.current;
    const card = track?.children?.[scrollIntoViewIndex];
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    return undefined;
  }, [scrollIntoViewNonce, scrollIntoViewIndex]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  const rootClasses = ['spark-carousel', className].filter(Boolean).join(' ');

  return (
    <div className={rootClasses}>
      {showLeft && (
        <Button
          variant="ghost"
          size="small"
          type="button"
          className="spark-carousel-chevron spark-carousel-chevron-left"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
          startIcon={<SparkIcon slug="chevron-left" size="small" />}
        />
      )}
      <div ref={scrollRef} className="spark-carousel-track">
        {children}
      </div>
      {showRight && (
        <Button
          variant="ghost"
          size="small"
          type="button"
          className="spark-carousel-chevron spark-carousel-chevron-right"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
          startIcon={<SparkIcon slug="chevron-right" size="small" />}
        />
      )}
    </div>
  );
}
